import { GraphQLClient } from "graphql-request";

import { useFeatureFlagGlobalStore } from "@/domain/_shared/feature-flag/hooks/use-feature-flag-global-store";
import type { GraphqlClientSource } from "@ark/shared";

import { getAccessToken } from "../storage.service";

import { getGraphqlClientContextHeaders } from "./graphql-client-context";

const isDebugDashboardSpeedEnabled = (): boolean =>
  useFeatureFlagGlobalStore.getState().isFeatureFlagEnabled("debug-dashboard-speed");

let apiEndpointOverride: string | undefined;

export function setApiEndpointOverride(endpoint: string | undefined): void {
  if (!endpoint) {
    apiEndpointOverride = undefined;
    return;
  }
  apiEndpointOverride = endpoint.replace(/\/graphql\/?$/, "").replace(/\/$/, "");
}

export const getBackendEndpoint = () => import.meta.env.VITE_API_ENDPOINT;

export const getApiEndpoint = () => {
  if (apiEndpointOverride !== undefined) {
    return apiEndpointOverride;
  }
  // This is needed for the public embeds coming from klimaportal.eu
  if (typeof window !== "undefined" && window.location.hostname.includes("klimaportal.eu")) {
    return `${window.location.origin}/graphql`;
  }
  const endpoint = import.meta.env.VITE_API_ENDPOINT;
  return endpoint;
};

/**
 * Legacy export — kept as a constant for back-compat with callers that use it
 * in template literals (e.g. `${API_ENDPOINT}/graphql/...`). New code should
 * call `getApiEndpoint()` so the SSR pod's runtime override is respected.
 */
export const API_ENDPOINT = getApiEndpoint();

// Create a GraphQL client with authentication. The URL is resolved per request
// via `getApiEndpoint()` so the SSR pod's `setApiEndpointOverride()` is honored
// even though the client is constructed at module load.
export const graphqlClient = new GraphQLClient(`${getApiEndpoint()}/graphql`, {
  requestMiddleware: (request) => {
    request.url = `${getApiEndpoint()}/graphql`;
    request.headers = {
      ...request.headers,
      ...getGraphqlClientContextHeaders(),
    };
    const token = getAccessToken();
    if (token) {
      request.headers = {
        ...request.headers,
        authorization: `Bearer ${token}`,
      };
    }
    if (isDebugDashboardSpeedEnabled()) {
      request.headers = {
        ...request.headers,
        "x-debug-timing": "1",
      };
    }
    return request;
  },
});

// For public queries (no authentication required)
export const publicGraphqlClient = new GraphQLClient(`${getApiEndpoint()}/graphql`, {
  requestMiddleware: (request) => {
    request.url = `${getApiEndpoint()}/graphql`;
    request.headers = {
      ...request.headers,
      ...getGraphqlClientContextHeaders(),
    };
    return request;
  },
});

export type OperationClient = GraphQLClient;

// Helper function to create GraphQL clients with operation-specific URLs
export const createOperationClient = (
  operationName: string,
  isPublic = false,
  options?: { dashboardId?: string; source?: GraphqlClientSource },
): OperationClient => {
  const client = new GraphQLClient(`${getApiEndpoint()}/graphql/${operationName}`, {
    requestMiddleware: (request) => {
      // Ensure proper headers for GraphQL Server
      request.headers = {
        ...request.headers,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...getGraphqlClientContextHeaders(options?.source),
      };

      // Add operation name to the request body if it's not already there
      if (request.body && typeof request.body === "string") {
        try {
          const bodyObj = JSON.parse(request.body);

          // Only add operationName if it doesn't exist and the body has the expected GraphQL structure
          if (!bodyObj.operationName && (bodyObj.query || bodyObj.mutation)) {
            bodyObj.operationName = operationName;
            request.body = JSON.stringify(bodyObj);
          }
        } catch {
          // If parsing fails, leave the body as is so the upstream request runs unchanged.
        }
      }

      if (!isPublic) {
        const token = getAccessToken();
        if (token) {
          request.headers = {
            ...request.headers,
            authorization: `Bearer ${token}`,
          };
        }
        if (isDebugDashboardSpeedEnabled()) {
          request.headers = {
            ...request.headers,
            "x-debug-timing": "1",
          };
        }
      } else {
        // Mark as public query so backend can apply public query security rules
        request.headers = {
          ...request.headers,
          "x-public": "1",
        };

        // Add dashboard ID header for public queries that require it
        if (options?.dashboardId) {
          request.headers = {
            ...request.headers,
            "x-dashboard-id": options.dashboardId,
          };
        }
      }
      return request;
    },
  });
  return client;
};
